import { FC, PropsWithChildren, ReactNode } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Modal, Paper, SxProps, Theme, Typography } from '@mui/material';

import { CloseIcon } from 'shared/assets/icons';
import useOutsideClick from 'shared/hooks/useOutsideClick';

import LoginBGDark from '../../assets/icons/login-bg-dark.svg';
import CloseModal from '../close-modal-window';

export interface IModalProps {
  isOpen: boolean;
  close: (isReset?: boolean) => void;
}

interface IProps extends PropsWithChildren, IModalProps {
  titleText: string;
  titleLogo?: ReactNode;
  subtitleText?: string;
  subButton?: boolean;
  hideBackdrop?: boolean;
  hideCloseIcon?: boolean;
  isDark?: boolean;
  showBG?: boolean;
  reset?: () => void;
  maxWidth?: number;
  isConfirmationNeeded?: boolean;
  onBack?: () => void;
  isPending?: boolean;
  sx?: SxProps<Theme>;
}

export const ModalTemplate: FC<IProps> = ({
  isOpen,
  titleText,
  titleLogo,
  subtitleText,
  children,
  hideBackdrop,
  close,
  hideCloseIcon,
  isDark,
  showBG,
  reset,
  maxWidth,
  onBack,
  isPending,
  isConfirmationNeeded = false,
  sx
}) => {
  const { ref, showConfirm, setShowConfirm, handleConfirm } = useOutsideClick(
    isConfirmationNeeded,
    false,
    () => {
      if (isPending) return undefined;
      close(true);
    },
    'MuiModal-backdrop'
  );

  const handleConfirmClose = () => {
    handleConfirm(true);
    reset?.();
  };

  const handleCancelClose = () => {
    handleConfirm(false);
  };

  return (
    <Modal
      open={isOpen}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: showBG
          ? isDark
            ? `no-repeat 50% 50% url(${LoginBGDark}) cover `
            : 'var(--mui-palette-background-login)'
          : '',
        ...sx
      }}
      hideBackdrop={hideBackdrop}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      style={{ overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
      {showConfirm ? (
        <CloseModal handleConfirmClose={handleConfirmClose} handleCancelClose={handleCancelClose} />
      ) : (
        <Paper
          sx={{
            borderRadius: 4,
            width: maxWidth ? '100%' : 413,
            position: 'relative',
            maxWidth,
            overflow: 'visible',
            margin: 'auto'
          }}>
          <Box ref={isPending ? null : ref} overflow={'hidden'} maxHeight={'90vh'}>
            {!isPending && !!onBack && (
              <Box
                className='modal-backArrowIcon'
                sx={{
                  display: 'block',
                  position: 'absolute',
                  top: '16px',
                  left: '17px',
                  width: 25,
                  height: 25,
                  cursor: 'pointer',
                  color: 'var(--mui-palette-primary-main)',
                  '&:hover': { color: 'var(--mui-palette-primary-diffs-hover)' },
                  '&:active': { color: 'var(--mui-palette-primary-diffs-active)' }
                }}
                onClick={onBack}>
                <ArrowBackIcon />
              </Box>
            )}

            {!isPending && (
              <Box
                className='modal-closeIcoon'
                sx={{
                  display: (hideCloseIcon && 'none') || 'block',
                  position: 'absolute',
                  top: '11.8px',
                  right: '11.8px',
                  width: 25,
                  height: 25,
                  color: 'var(--mui-palette-modal-closeIcon)',
                  '&:hover': { color: 'var(--mui-palette-modal-closeIconHover)' },
                  '&:active': { color: 'var(--mui-palette-modal-closeIconActive)' },
                  cursor: 'pointer'
                }}
                onClick={() => {
                  isConfirmationNeeded ? setShowConfirm(true) : close();
                }}>
                <CloseIcon />
              </Box>
            )}
            <Box className='modal-title-wrapper' sx={{ paddingTop: '24px', borderRadius: 4 }}>
              {titleLogo}
              <Typography
                component='h1'
                className={'modal-title'}
                sx={{
                  fontSize: ' 1.5rem',
                  lineHeight: '2rem',
                  textAlign: 'center',
                  p: '0 5px',
                  color: 'var(--mui-palette-text-modalDark)'
                }}>
                {titleText}
              </Typography>
              {subtitleText && (
                <Typography
                  sx={{
                    mt: 4,
                    textAlign: 'center',
                    color: 'var(--mui-palette-text-secondary)',
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}>
                  {subtitleText}
                </Typography>
              )}
            </Box>
            {children}
          </Box>
        </Paper>
      )}
    </Modal>
  );
};
